import React from "react";
import { Form, OverlayTrigger, Tooltip } from "react-bootstrap";
import { ResponsiveDataList } from "../responsive";
import Helpers from "../Helpers";
import DeleteModel from "../DeleteModal";
import AlertDelete from "../AlertDelete";
import MasterDataConfirmationModal from "../MasterDataConfirmationModal";
import MasterPageShell from "./MasterPageShell";
import MasterSectionCard from "./MasterSectionCard";
import MasterSearchField from "./MasterSearchField";
import MasterRowActions from "./MasterRowActions";
import MasterFormActions from "./MasterFormActions";
import MasterDetailBlock from "./MasterDetailBlock";
import { BilingualMasterCard } from "./cards/BilingualMasterCard";
import styles from "./MasterData.module.scss";

interface BilingualMasterPageProps {
  isAdmin: boolean;
  formTitle: string;
  listTitle: string;
  listSubtitle?: string;
  formIcon?: React.ReactNode;
  listIcon?: React.ReactNode;
  englishLabel: string;
  englishPlaceholder: string;
  englishRequiredMessage: string;
  englishField: string;
  spanishLabel: string;
  spanishPlaceholder: string;
  spanishField: string;
  englishColumnName: string;
  spanishColumnName: string;
  createdField?: string;
  createdSortFormat?: string;
  dataKey: string;
  deleteTitle: string;
  deleteDetailLabel: string;
  deleteAlertHeader: string;
  deleteAlertMessage: React.ReactNode;
  isError: boolean;
  validated: boolean;
  cp: boolean;
  icon_id: any;
  list: any[];
  show: boolean;
  LoadData: boolean;
  deleteAlert: boolean;
  matches: any[];
  showConfirmationModal: boolean;
  setShowConfirmationModal: (open: boolean) => void;
  data: Record<string, any>;
  handleClose: () => void;
  handleCloseAlert: () => void;
  handleShow: (item: any) => void;
  handleChanges: (e: any) => void;
  handleSubmit: (e: any) => void;
  handleConfirmSubmit: () => void;
  CollectData: (item: any) => void;
  DeleteData: () => void;
  SearchData: (event: any) => void;
  clearfield: () => void;
  searchValue?: string;
}

const renderTooltip = (props: any) => (
  <Tooltip id="button-tooltip">{props}</Tooltip>
);

export default function BilingualMasterPage({
  isAdmin,
  formTitle,
  listTitle,
  listSubtitle,
  formIcon,
  listIcon,
  englishLabel,
  englishPlaceholder,
  englishRequiredMessage,
  englishField,
  spanishLabel,
  spanishPlaceholder,
  spanishField,
  englishColumnName,
  spanishColumnName,
  createdField = "createdAt",
  createdSortFormat,
  dataKey,
  deleteTitle,
  deleteDetailLabel,
  deleteAlertHeader,
  deleteAlertMessage,
  isError,
  validated,
  cp,
  icon_id,
  list,
  show,
  LoadData,
  deleteAlert,
  matches,
  showConfirmationModal,
  setShowConfirmationModal,
  data,
  handleClose,
  handleCloseAlert,
  handleShow,
  handleChanges,
  handleSubmit,
  handleConfirmSubmit,
  CollectData,
  DeleteData,
  SearchData,
  clearfield,
  searchValue,
}: BilingualMasterPageProps) {
  const isEditing = icon_id !== "";

  const columns = [
    ...(isAdmin
      ? [
          {
            id: 3,
            name: "Action",
            button: true,
            cell: (row: any) => (
              <MasterRowActions
                onEdit={() => CollectData(row)}
                onDelete={() => handleShow(row)}
              />
            ),
          },
        ]
      : []),
    {
      id: 1,
      name: englishColumnName,
      selector: (row: any) => row[englishField],
      cell: (row: any) => (
        <OverlayTrigger
          placement="right"
          delay={{ show: 250, hide: 400 }}
          overlay={renderTooltip(row[englishField])}
        >
          <span>{row[englishField]}</span>
        </OverlayTrigger>
      ),
      sortable: true,
      reorder: true,
      tag: "allowRowEvents",
      style: { cursor: "text" },
    },
    {
      id: 4,
      name: spanishColumnName,
      selector: (row: any) => row[spanishField],
      cell: (row: any) => (
        <OverlayTrigger
          placement="right"
          delay={{ show: 250, hide: 400 }}
          overlay={renderTooltip(row[spanishField] || "")}
        >
          <span>{row[spanishField]}</span>
        </OverlayTrigger>
      ),
      sortable: true,
      reorder: true,
      tag: "allowRowEvents",
      style: { cursor: "text" },
    },
    {
      id: 2,
      name: "CreatedDate",
      selector: (row: any) =>
        createdSortFormat
          ? Helpers.convertUtcToTimezone(row[createdField], createdSortFormat)
          : (Helpers.convertUtcToTimezone as any)(row[createdField]),
      cell: (row: any) => (
        <OverlayTrigger
          placement="bottom"
          delay={{ show: 250, hide: 400 }}
          overlay={renderTooltip(
            Helpers.convertUtcToTimezone(
              row[createdField],
              "MM-DD-YYYY hh:mm:a",
            ),
          )}
        >
          <span>
            {row[createdField]
              ? Helpers.convertUtcToTimezone(row[createdField], "MM-DD-YYYY")
              : ""}
          </span>
        </OverlayTrigger>
      ),
      sortable: true,
      style: { cursor: "text" },
    },
  ];

  const conditionalRowStyles = [
    {
      when: (row: any) =>
        row.accountStatus === "deceased" ||
        row.accountStatus === "divorced" ||
        row.accountStatus === "inactive",
      style: {
        backgroundColor: "#f8d7da",
        color: "#000",
        borderColor: "#dfc2c4",
      },
    },
  ];

  return (
    <MasterPageShell>
      {isAdmin ? (
        <MasterSectionCard
          title={isEditing ? `Edit ${formTitle.replace(/^Add\s+/i, "")}` : formTitle}
          subtitle="English name is required. Spanish is optional."
          icon={formIcon}
          bgcolor="#e8f3fc"
        >
          <Form
            onSubmit={handleSubmit}
            noValidate
            validated={validated}
            className="forms-sample"
          >
            <div className={styles.formGrid}>
              <Form.Group>
                <Form.Label>{englishLabel}</Form.Label>
                <Form.Control
                  required
                  size="sm"
                  type="text"
                  name={englishField}
                  id={englishField}
                  placeholder={englishPlaceholder}
                  value={data[englishField] || ""}
                  className="form-control"
                  onChange={handleChanges}
                />
                <Form.Control.Feedback type="invalid">
                  {englishRequiredMessage}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group>
                <Form.Label>{spanishLabel}</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  name={spanishField}
                  id={spanishField}
                  placeholder={spanishPlaceholder}
                  value={data[spanishField] || ""}
                  className="form-control"
                  onChange={handleChanges}
                />
              </Form.Group>
            </div>
            <MasterFormActions
              isEditing={isEditing}
              onCancel={clearfield}
              disabled={cp || isError}
              showCancel={isEditing}
            />
          </Form>
        </MasterSectionCard>
      ) : null}

      <MasterSectionCard
        title={listTitle}
        subtitle={listSubtitle}
        icon={listIcon}
        total={list?.length}
        bgcolor="#f3f7fb"
        toolbar={
          <MasterSearchField
            onChange={SearchData}
            value={searchValue}
            placeholder="Search..."
          />
        }
        bodyPadding={false}
      >
        <div className={styles.tableWrap}>
          <ResponsiveDataList
            data={list}
            columns={columns}
            conditionalRowStyles={conditionalRowStyles}
            pointerOnHover
            highlightOnHover
            loading={LoadData}
            isServerSide={false}
            pagination
            countPerPage={20}
            cardGridColumns={{ xs: "1fr", md: "repeat(2, minmax(0, 1fr))" }}
            renderCard={(row) => (
              <BilingualMasterCard
                row={row}
                englishField={englishField}
                spanishField={spanishField}
                createdField={createdField}
                isAdmin={isAdmin}
                onEdit={CollectData}
                onDelete={handleShow}
              />
            )}
          />
        </div>
      </MasterSectionCard>

      <DeleteModel
        show={show}
        content={
          <MasterDetailBlock
            title={deleteTitle}
            label={deleteDetailLabel}
            value={icon_id?.[englishField]}
          />
        }
        handleClose={handleClose}
        DeleteData={DeleteData}
      />
      {deleteAlert ? (
        <AlertDelete
          show={deleteAlert}
          alertHeader={deleteAlertHeader}
          alerMessage={deleteAlertMessage}
          handleClose={handleCloseAlert}
          buttonText={"Close"}
        />
      ) : null}

      <MasterDataConfirmationModal
        show={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={handleConfirmSubmit}
        matches={matches}
        dataKey={dataKey}
      />
    </MasterPageShell>
  );
}
